/**
 * The chosen size, remembered on this screen.
 *
 * Applied as an attribute on the root element, so every surface — board, cards,
 * station columns, both modals — follows one switch. Nothing is sent to the
 * server: the monitor by the pass and the one at the bar are different screens
 * read from different distances, and each keeps its own answer.
 */
import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_KDS_SCALE,
  KDS_SCALE_STORAGE_KEY,
  nextKdsScale,
  parseKdsScale,
  type KdsScale,
} from "../lib/kds-scale";

function read(): KdsScale {
  try {
    return parseKdsScale(window.localStorage.getItem(KDS_SCALE_STORAGE_KEY));
  } catch {
    // Private window, storage disabled, a browser that refuses: the board still
    // has to render, at the size it has always had.
    return DEFAULT_KDS_SCALE;
  }
}

export function useKdsScale() {
  const [scale, setScale] = useState<KdsScale>(read);

  useEffect(() => {
    document.documentElement.setAttribute("data-kds-scale", scale);
    try {
      window.localStorage.setItem(KDS_SCALE_STORAGE_KEY, scale);
    } catch {
      // The size still applies for this session; only remembering it fails.
    }
  }, [scale]);

  const cycle = useCallback(() => setScale((current) => nextKdsScale(current)), []);

  return { scale, setScale, cycle };
}
